import React, { useState, useRef, useEffect } from 'react';
import './Combobox.css';

/**
 * Reusable Combobox component for searchable dropdowns
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of string options
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string|Array} props.value - Selected value (string) or values (array)
 * @param {Array} props.selectedValues - Selected values for multiple mode
 * @param {boolean} props.allowCustomValues - Whether to allow values not in the options list
 * @param {boolean} props.multiple - Whether to allow multiple selections
 * @param {string} props.id - ID for the input element
 */
const Combobox = ({ 
  options = [], 
  onChange, 
  placeholder = 'Select an option', 
  value = '',
  selectedValues = [],
  allowCustomValues = false,
  multiple = false,
  id
}) => {
  // For single selection mode
  const [inputValue, setInputValue] = useState('');
  // For multiple selection mode
  const [multipleValues, setMultipleValues] = useState(selectedValues || []);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const comboboxRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize input value from props
  useEffect(() => {
    if (!multiple && value) {
      setInputValue(value);
    }
  }, [value, multiple]);

  // Initialize multiple values from props
  useEffect(() => {
    if (multiple && selectedValues) {
      setMultipleValues(selectedValues);
    }
  }, [selectedValues, multiple]);

  // Filter options based on input
  useEffect(() => {
    if (!isOpen) return;

    const searchTerm = inputValue.toLowerCase();
    const filtered = options.filter(option => 
      option.toLowerCase().includes(searchTerm)
    );
    
    setFilteredOptions(filtered);
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  }, [inputValue, options, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (!isOpen && newValue) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    if (multiple) {
      // For multiple selection, add to array if not already there
      if (!multipleValues.includes(option)) {
        const newValues = [...multipleValues, option];
        setMultipleValues(newValues);
        onChange(newValues);
      }
      setInputValue('');
    } else {
      // For single selection, replace value
      setInputValue(option);
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        e.preventDefault();
        break;
      case 'ArrowUp':
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        e.preventDefault();
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex]);
          e.preventDefault();
        } else if (allowCustomValues && inputValue.trim()) {
          // Add custom value if allowed
          handleOptionClick(inputValue.trim());
          e.preventDefault();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        e.preventDefault();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Add a custom value when Enter is pressed in the input
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && allowCustomValues && inputValue.trim() && !filteredOptions.includes(inputValue.trim())) {
      if (multiple) {
        if (!multipleValues.includes(inputValue.trim())) {
          const newValues = [...multipleValues, inputValue.trim()];
          setMultipleValues(newValues);
          onChange(newValues);
          setInputValue('');
        }
      } else {
        setInputValue(inputValue.trim());
        onChange(inputValue.trim());
        setIsOpen(false);
      }
      e.preventDefault();
    }
  };

  return (
    <div className="combobox-container" ref={comboboxRef}>
      <div className="combobox-input-wrapper">
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="combobox-input"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={(e) => {
            handleKeyDown(e);
            handleInputKeyDown(e);
          }}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            highlightedIndex >= 0 
              ? `${id}-option-${highlightedIndex}` 
              : undefined
          }
        />
        <button
          type="button"
          className="combobox-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close options' : 'Show options'}
        >
          <span className={`combobox-arrow ${isOpen ? 'open' : ''}`}></span>
        </button>
      </div>
      
      {isOpen && (
        <ul 
          className="combobox-options" 
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Options list"
          tabIndex="-1"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={index}
                id={`${id}-option-${index}`}
                className={`combobox-option ${highlightedIndex === index ? 'highlighted' : ''}`}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={highlightedIndex === index}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="combobox-no-results">
              {allowCustomValues 
                ? 'No matches found. Press Enter to add this as a new option.' 
                : 'No matches found.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default Combobox;
